import {
    ANALYTICS_REGISTRATION_PAGE,
    ANALYTICS_REGISTRATION_BTN_CLICKED,
    ANALYTICS_REGISTRATION_CLOSED,
    ANALYTICS_ACTION_BUTTON_CLICKED,
    ANALYTICS_HYBRID_APP_EVENT,
    ANALYTICS_ELAPSED_TIME,
} from 'aria-viewer/analytics/analyticActions'
import { trackEvent } from 'aria-viewer/core/middleware/analyticsMiddleware'
import * as EVENTS from 'aria-viewer/analytics/constants/mobileAnalytics'
import { DEVICE_ORIENTATION_CHANGED } from 'aria-viewer/analytics/constants'
import { getTourData, getMobileDefaultProperties } from 'aria-viewer/analytics/utils/analyticUtils'
import RegistrationPageContexts from 'aria-viewer/registration/RegistrationPageContexts'
import CTAPromptTypes from 'aria-viewer/tour/constants/CTAPromptTypes'
import { SHOW_SLIDE_PANEL } from 'aria-viewer/slidepanel/slidePanelActions'
import { STOP_LIST_DRAWER } from 'aria-viewer/drawer/drawerMenuActions'
import StopCardStates from 'aria-viewer/stopselector/constants/StopCardStates'
import { REGISTER, FINISHED } from 'aria-viewer/registration/registrationWrapper'
import { ORIENTATION_CHANGED } from 'aria-viewer/device/deviceActions'

const registrationPageData = (getState, action) => {
    const state = getState()
    let properties = getMobileDefaultProperties()(getState)
    const { stoptitle } = getTourData()(getState)
    const currentModal = state.getIn(['modal', 'modalStack']).get(0)
    const { prompt } = (currentModal && currentModal.content) || {}
    const { page, name, data } = action

    properties = {
        ...properties,
        Stop: stoptitle,
        currentModal,
        prompt,
        page,
        name,
        data,
    }
    if (prompt) {
        const Skipped_stop = state.getIn(['tour', 'stops', 'current'])
        const Curr_stop = state.getIn(['tour', 'stops', 'previous'])
        properties = {
            ...properties,
            Skipped_stop,
            Curr_stop,
        }
    }
    return properties
}

/**
 * returns initiatedby for cta buttons
 */
const getCTAInitiatedBy = state => {
    const initTypes = {
        drawer: 'Drawer',
        welcome: 'Welcome',
        exp: 'Experience Home',
        [CTAPromptTypes.WHATS_NEXT]: "What's Next",
        [CTAPromptTypes.ACT_NOW]: 'Act Now',
    }

    // if a CTA Prompt is open (Act Now/Whats Next)
    if (state.getIn(['ctaPrompt', 'isCTAPromptOpen'])) {
        // first parse out the string to send to analytics
        const ctaAnalyticsValue = initTypes[state.getIn(['ctaPrompt', 'ctaPromptType'])]
        return getInitiatedObj(ctaAnalyticsValue)
    }
    const isSlidePanelOpen = state.getIn(['slidePanel', 'isSlidePanelOpen']) == SHOW_SLIDE_PANEL

    const tour = state.get('tour')
    const tourWelcome = tour.get('welcomePrompt')
    const registrationWelcome = tour.getIn(['registration', 'welcomePrompt'])

    if (isSlidePanelOpen) {
        return getInitiatedObj(initTypes['drawer'])
    } else if (tourWelcome || registrationWelcome) {
        return getInitiatedObj(initTypes['welcome'])
    } else {
        return getInitiatedObj(initTypes['exp'])
    }
}

const getInitiatedObj = initiated => ({
    Initiated_by: initiated,
})

/**
 * Returns the initiated by object based on card state and/or
 * stop drawer.
 */
const getStopCardInitiatedBy = state => {
    const initiatedTypes = {
        list: 'List',
        expanded: 'Expand_card',
        peek: 'Contra_card',
        lower: 'Contra_card',
    }

    const cardStateMapping = {
        [StopCardStates.SHOW]: initiatedTypes['expanded'],
        [StopCardStates.PEEK]: initiatedTypes['peek'],
        [StopCardStates.LOWER]: initiatedTypes['lower'],
    }

    const drawerMenu = state.getIn(['drawerMenu', 'name'])

    if (drawerMenu == STOP_LIST_DRAWER) {
        return getInitiatedObj(initiatedTypes['list'])
    }

    const stopCardState = state.getIn(['stopCards', 'state'])
    return getInitiatedObj(cardStateMapping[stopCardState])
}

const analyticAnalytics = {
    // User advanced to another page in the registration form.
    [ANALYTICS_REGISTRATION_PAGE]: (getState, action) => {
        const state = getState()

        const { Stop, prompt, page, Skipped_stop, Curr_stop } = registrationPageData(getState, action)
        let properties = getMobileDefaultProperties()(getState)
        const { welcomePrompt } = getTourData()(getState)

        let event
        properties = {
            ...properties,
            Stop,
            Skipped_stop,
            Curr_stop,
        }

        switch (page) {
            case RegistrationPageContexts.MOBILE_LANDING:
                event = EVENTS.M_MOBEXP_REGWELCOME_VIEWED
                break
            case RegistrationPageContexts.DESKTOP_LANDING:
                event = prompt
                    ? EVENTS.M_SKIPSTOPREG_POPUP_VIEWED
                    : welcomePrompt
                    ? EVENTS.M_WELREG_POPUP_VIEWED
                    : EVENTS.M_DIRREG_POPUP_VIEWED
                break
            case RegistrationPageContexts.AFTER_LANDING:
                event = prompt
                    ? EVENTS.M_SKIPSTOPREG_STEP2_VIEWED
                    : welcomePrompt
                    ? EVENTS.M_WELREG_STEP2_VIEWED
                    : EVENTS.M_DIRREG_STEP2_VIEWED
                break
            default:
                return
        }

        return trackEvent(
            event,
            prompt && page == RegistrationPageContexts.DESKTOP_LANDING
                ? { ...properties, ...getStopCardInitiatedBy(state) }
                : properties
        )
    },

    [ANALYTICS_ACTION_BUTTON_CLICKED]: (getState, action) => {
        const { name, color } = action
        let properties = getMobileDefaultProperties()(getState)
        const { stoptitle } = getTourData()(getState)

        properties = {
            ...properties,
            Stop: stoptitle,
            CTA_name: name,
            Color: color,
        }
        return trackEvent(EVENTS.M_MOBEXP_CTABUTTON_TAPPED, { ...properties, ...getCTAInitiatedBy(getState()) })
    },

    // Action triggered to alert how much time has elapsed for the purposes
    // of analytics
    [ANALYTICS_ELAPSED_TIME]: (getState, action) => {
        let properties = getMobileDefaultProperties()(getState)
        const { stoptitle } = getTourData()(getState)

        return trackEvent(EVENTS.M_EXP_10_SECOND_MARK, {
            ...properties,
            Stop: stoptitle,
        })
    },

    [ANALYTICS_HYBRID_APP_EVENT]: (getState, action) => {
        let properties = getMobileDefaultProperties()(getState)
        const { type, isClient, clientProperties } = getTourData()(getState)

        if (action && action.event) {
            properties = {
                ...properties,
                Exp_Type: type,
                Is_Client: isClient,
            }

            const shouldIncludeAppAvailable = [
                EVENTS.M_MOBEXP_SIDEWAY_VISIBLE,
                EVENTS.M_MOBEXP_SIDEWAY_CLICKED,
            ].includes(action.event)

            if (shouldIncludeAppAvailable) {
                properties = {
                    ...properties,
                    App_Available: clientProperties.isAppAvailable,
                }
            }

            return trackEvent(action.event, properties)
        } else {
            throw 'action or action.event is undefined'
        }
    },

    [ANALYTICS_REGISTRATION_CLOSED]: (getState, action) => {
        const state = getState()

        const { Stop, prompt, page, Skipped_stop, Curr_stop } = registrationPageData(getState, action)
        let properties = getMobileDefaultProperties()(getState)
        const { welcomePrompt } = getTourData()(getState)

        let event
        properties = {
            ...properties,
            Skipped_stop,
            Curr_stop,
        }

        switch (page) {
            case RegistrationPageContexts.MOBILE_LANDING:
                event = EVENTS.M_MOBEXP_REGWELCOME_CLOSED
                if (prompt) {
                    properties = { ...properties, ...getStopCardInitiatedBy(state) }
                }
                break
            case RegistrationPageContexts.DESKTOP_LANDING:
                event = prompt
                    ? EVENTS.M_SKIPSTOPREG_POPUP_CLOSED
                    : welcomePrompt
                    ? EVENTS.M_WELREG_POPUP_CLOSED
                    : EVENTS.M_DIRREG_POPUP_CLOSED
                if (prompt) {
                    properties = { ...properties, ...getStopCardInitiatedBy(state) }
                }
                break
            case RegistrationPageContexts.AFTER_LANDING:
                event = prompt
                    ? EVENTS.M_SKIPSTOPREG_STEP2_CLOSED
                    : welcomePrompt
                    ? EVENTS.M_WELREG_STEP2_CLOSED
                    : EVENTS.M_DIRREG_STEP2_CLOSED
                break
            default:
                return
        }

        if (!prompt) {
            properties = { ...properties, ...{ Stop } }
        }
        const tracked = trackEvent(event, properties)
        return prompt ? [tracked, trackEvent(EVENTS.SKIPPED_REGISTRATION)] : tracked
    },

    // Determines when a registration button was clicked.
    [ANALYTICS_REGISTRATION_BTN_CLICKED]: (getState, action) => {
        const state = getState()
        let properties = getMobileDefaultProperties()(getState)
        const { Stop, prompt, name, data, Skipped_stop, Curr_stop } = registrationPageData(getState, action)

        const { welcomePrompt } = getTourData()(getState)

        let event

        properties = {
            ...properties,
            Stop,
            Skipped_stop,
            Curr_stop,
        }

        switch (name) {
            case REGISTER:
                if (prompt) {
                    properties = { ...properties, ...getStopCardInitiatedBy(state) }
                }
                event = prompt
                    ? EVENTS.M_SKIPSTOPREG_REGBUTTON_TAPPED
                    : welcomePrompt
                    ? EVENTS.M_WELREG_REGBUTTON_TAPPED
                    : EVENTS.M_DIRREG_REGBUTTON_TAPPED
                break

            case FINISHED:
                event = prompt
                    ? EVENTS.M_SKIPSTOPREG_FINISHEDBUTTON_TAPPED
                    : welcomePrompt
                    ? EVENTS.M_WELREG_FINISHEDBUTTON_TAPPED
                    : EVENTS.M_DIRREG_FINISHEDBUTTON_TAPPED
                break

            default:
                return
        }

        return trackEvent(event, properties)
    },

    [ORIENTATION_CHANGED]: (getState, action) => {
        const { orientation } = action

        return trackEvent(DEVICE_ORIENTATION_CHANGED, {
            Device_Orientation: orientation,
        })
    },
}

export default analyticAnalytics
